import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { scopeFor } from "@sts/schemas";
import type { JobMapPin, JobsMapResponse, UnplacedReason } from "../api/share-map-types.js";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { href } from "../app/router.js";
import { Async } from "../components/Async.jsx";
import { longDate } from "../lib/format.js";
import { STAGE_LABEL } from "../lib/stages.js";
import {
  BAND_COLOUR,
  BAND_LABEL,
  BAND_ORDER,
  STAGE_BAND,
  type StageBand,
} from "../lib/stage-bands.js";
import type { JobsMapHandle, MapPin } from "../lib/leaflet-map.js";

/**
 * The jobs map: every job this organisation holds, pinned where the property
 * is — the company's private library of work, on a map.
 *
 * Private by construction. The API returns the organisation's OWN jobs only
 * (a surveyor or installer gets their own bookings, as on the pipeline), and
 * nothing here is shared, published or indexed.
 *
 * WHERE PINS COME FROM is the API's business (`crm/jobs-map.ts`): the
 * property postcode, geocoded once through postcodes.io and cached, else the
 * average of the job's trustworthy photograph fixes. A job it cannot place is
 * still listed, with the reason — nothing drops silently off the map.
 *
 * OFFLINE: the map is an enhancement over the list, not the other way round.
 * The list shows every job with the same filters, the same colours in words
 * and the same summary; if Leaflet cannot load or the tiles cannot be fetched
 * the screen says so and shows the list.
 */
export function JobsMap(): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<JobsMapResponse>(async () => {
    const me = await client.me();
    const mine = scopeFor(me.role, "job", "read") !== "org";
    return client.jobsMap({ mine });
  }, [client]);

  return (
    <>
      <div className="page-header">
        <h1>Jobs map</h1>
        <button className="secondary" onClick={reload}>
          Refresh
        </button>
        <p className="lede">
          Every job, pinned at its property and coloured by pipeline stage.
          Private to this organisation.
        </p>
      </div>
      <Async state={state} what="the jobs map" reload={reload}>
        {(data) => <MapBody data={data} />}
      </Async>
    </>
  );
}

const UNPLACED_WORDS: Record<UnplacedReason, string> = {
  "no-postcode": "No usable postcode on the property, and no photograph with a GPS fix",
  "postcode-not-found": "Postcode not in the UK postcode directory, and no photograph with a GPS fix",
  pending: "Postcode not located yet — refresh in a moment",
};

type View = "map" | "list";
const ALL_BANDS: ReadonlySet<StageBand> = new Set(BAND_ORDER);

function MapBody({ data }: { readonly data: JobsMapResponse }): ReactNode {
  const [bands, setBands] = useState<ReadonlySet<StageBand>>(ALL_BANDS);
  const [surveyor, setSurveyor] = useState<string>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [view, setView] = useState<View>("map");
  const [tilesFailed, setTilesFailed] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const surveyors = useMemo(() => {
    const byId = new Map<string, string>();
    for (const job of data.jobs) if (job.surveyor) byId.set(job.surveyor.id, job.surveyor.name);
    return [...byId].sort((a, b) => a[1].localeCompare(b[1]));
  }, [data.jobs]);

  const filtered = useMemo(
    () =>
      data.jobs.filter((job) => {
        if (!bands.has(STAGE_BAND[job.stage])) return false;
        if (surveyor === "none" && job.surveyor !== null) return false;
        if (surveyor !== "all" && surveyor !== "none" && job.surveyor?.id !== surveyor) return false;
        const day = job.createdAt.slice(0, 10);
        if (from && day < from) return false;
        if (to && day > to) return false;
        return true;
      }),
    [data.jobs, bands, surveyor, from, to],
  );

  const placed = filtered.filter((j) => j.location !== null);
  const unplaced = filtered.filter((j) => j.location === null);
  const selected = filtered.find((j) => j.jobId === selectedId) ?? null;
  const showMap = view === "map" && !tilesFailed;

  const toggleBand = (band: StageBand) => {
    const next = new Set(bands);
    if (next.has(band)) next.delete(band);
    else next.add(band);
    setBands(next);
  };

  const filtering = bands.size !== BAND_ORDER.length || surveyor !== "all" || from !== "" || to !== "";

  return (
    <>
      <GeocodingNotes data={data} />

      <fieldset className="jobs-map__filters">
        <legend className="visually-hidden">Filter the jobs</legend>
        <div className="jobs-map__bands" role="group" aria-label="Pipeline stage">
          {BAND_ORDER.map((band) => (
            <label key={band} className="jobs-map__band">
              <input type="checkbox" checked={bands.has(band)} onChange={() => toggleBand(band)} />
              <span className="jobs-map__swatch" style={{ background: BAND_COLOUR[band] }} aria-hidden="true" />
              {BAND_LABEL[band]}
            </label>
          ))}
        </div>
        <div className="jobs-map__row">
          <label className="field">
            <span>Surveyor</span>
            <select value={surveyor} onChange={(e) => setSurveyor(e.target.value)}>
              <option value="all">Everyone</option>
              <option value="none">No surveyor booked</option>
              {surveyors.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Job created from</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="field">
            <span>to</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          {filtering ? (
            <button
              type="button"
              className="link"
              onClick={() => {
                setBands(ALL_BANDS);
                setSurveyor("all");
                setFrom("");
                setTo("");
              }}
            >
              Clear filters
            </button>
          ) : null}
        </div>
      </fieldset>

      <div className="spread jobs-map__bar">
        <p className="small" role="status" aria-live="polite">
          {filtered.length} {filtered.length === 1 ? "job" : "jobs"} · {placed.length} on the map
          {unplaced.length > 0 ? ` · ${unplaced.length} without a location` : ""}
        </p>
        <div role="group" aria-label="View" className="jobs-map__views">
          <button
            type="button"
            className={showMap ? "primary" : "secondary"}
            aria-pressed={showMap}
            disabled={tilesFailed}
            onClick={() => setView("map")}
          >
            Map
          </button>
          <button
            type="button"
            className={showMap ? "secondary" : "primary"}
            aria-pressed={!showMap}
            onClick={() => setView("list")}
          >
            List
          </button>
        </div>
      </div>

      {tilesFailed ? (
        <div className="notice" role="status">
          <p>
            The map could not be loaded — this computer may be offline, or map
            tiles are blocked on this network. Every job is in the list below.
          </p>
        </div>
      ) : null}

      <div className={`jobs-map__layout${selected ? " jobs-map__layout--selected" : ""}`}>
        <div className="jobs-map__main">
          {showMap ? (
            <>
              <MapCanvas
                jobs={placed}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onFailed={() => setTilesFailed(true)}
              />
              {unplaced.length > 0 ? (
                <details className="jobs-map__unplaced">
                  <summary>
                    {unplaced.length} {unplaced.length === 1 ? "job is" : "jobs are"} not on the map
                  </summary>
                  <JobList jobs={unplaced} selectedId={selectedId} onSelect={setSelectedId} />
                </details>
              ) : null}
            </>
          ) : (
            <JobList jobs={filtered} selectedId={selectedId} onSelect={setSelectedId} />
          )}
        </div>
        {selected ? <JobSummary job={selected} onClose={() => setSelectedId(null)} /> : null}
      </div>
    </>
  );
}

function GeocodingNotes({ data }: { readonly data: JobsMapResponse }): ReactNode {
  const { geocoding } = data;
  if (!geocoding.configured) {
    return (
      <p className="notice small" role="note">
        Postcode lookup is switched off on this server, so jobs are placed only
        from photographs taken on site.
      </p>
    );
  }
  if (geocoding.failed) {
    return (
      <p className="notice small" role="note">
        The postcode lookup service did not answer. Showing the jobs already
        located; refresh to try the rest again.
      </p>
    );
  }
  if (geocoding.pending > 0) {
    return (
      <p className="notice small" role="note">
        {geocoding.pending} {geocoding.pending === 1 ? "job is" : "jobs are"} still being
        located. Refresh in a moment to see {geocoding.pending === 1 ? "it" : "them"} on the map.
      </p>
    );
  }
  return null;
}

/** The Leaflet map, loaded on demand. Any failure to load it hands over to the list. */
function MapCanvas({
  jobs,
  selectedId,
  onSelect,
  onFailed,
}: {
  readonly jobs: readonly JobMapPin[];
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
  readonly onFailed: () => void;
}): ReactNode {
  const container = useRef<HTMLDivElement | null>(null);
  const [handle, setHandle] = useState<JobsMapHandle | null>(null);
  // The latest callbacks, read at call time so the map is built once.
  const callbacks = useRef({ onSelect, onFailed });
  callbacks.current = { onSelect, onFailed };

  useEffect(() => {
    let cancelled = false;
    let made: JobsMapHandle | null = null;
    import("../lib/leaflet-map.js").then(
      (mod) => {
        if (cancelled || !container.current) return;
        try {
          made = mod.createJobsMap(container.current, {
            onSelect: (id) => callbacks.current.onSelect(id),
            onTilesFailed: () => callbacks.current.onFailed(),
          });
          setHandle(made);
        } catch {
          callbacks.current.onFailed();
        }
      },
      () => {
        if (!cancelled) callbacks.current.onFailed();
      },
    );
    return () => {
      cancelled = true;
      made?.destroy();
    };
  }, []);

  const pins = useMemo<MapPin[]>(
    () =>
      jobs.flatMap((job) =>
        job.location
          ? [
              {
                id: job.jobId,
                latitude: job.location.latitude,
                longitude: job.location.longitude,
                colour: BAND_COLOUR[STAGE_BAND[job.stage]],
                label: `${job.reference} — ${STAGE_LABEL[job.stage]}`,
              },
            ]
          : [],
      ),
    [jobs],
  );

  useEffect(() => {
    handle?.setPins(pins);
  }, [handle, pins]);

  useEffect(() => {
    handle?.select(selectedId);
  }, [handle, selectedId, pins]);

  return (
    <div
      ref={container}
      className="jobs-map__canvas"
      role="region"
      aria-label="Map of jobs. Use the list view for a keyboard-accessible version."
    />
  );
}

function JobList({
  jobs,
  selectedId,
  onSelect,
}: {
  readonly jobs: readonly JobMapPin[];
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
}): ReactNode {
  if (jobs.length === 0) return <p className="empty">No jobs match these filters.</p>;
  return (
    <div className="table-scroll">
      <table className="jobs-map__table">
        <thead>
          <tr>
            <th scope="col">Job</th>
            <th scope="col">Stage</th>
            <th scope="col">Address</th>
            <th scope="col">Surveyor</th>
            <th scope="col">Placed by</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <tr key={job.jobId} aria-selected={job.jobId === selectedId}>
              <td>
                <button type="button" className="link" onClick={() => onSelect(job.jobId)}>
                  {job.reference}
                </button>
              </td>
              <td>
                <StageTag job={job} />
              </td>
              <td>{addressLine(job) ?? <span className="muted">No address</span>}</td>
              <td>{job.surveyor?.name ?? <span className="muted">—</span>}</td>
              <td className="small">{placedBy(job)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StageTag({ job }: { readonly job: JobMapPin }): ReactNode {
  const band = STAGE_BAND[job.stage];
  return (
    <span className="jobs-map__stage">
      <span className="jobs-map__swatch" style={{ background: BAND_COLOUR[band] }} aria-hidden="true" />
      {STAGE_LABEL[job.stage]}
    </span>
  );
}

function addressLine(job: JobMapPin): string | null {
  if (!job.address) return null;
  const parts = [job.address.line1, job.address.town, job.address.postcode].filter(
    (p): p is string => typeof p === "string" && p.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(", ") : null;
}

function placedBy(job: JobMapPin): string {
  if (job.location?.source === "postcode") return "Postcode";
  if (job.location?.source === "photo-gps") return "Photograph GPS";
  return UNPLACED_WORDS[job.unplacedReason ?? "pending"];
}

function JobSummary({ job, onClose }: { readonly job: JobMapPin; readonly onClose: () => void }): ReactNode {
  return (
    <aside className="jobs-map__summary" aria-labelledby="jobs-map-summary-heading">
      <div className="spread">
        <h2 id="jobs-map-summary-heading">{job.reference}</h2>
        <button type="button" className="link" onClick={onClose}>
          Close
        </button>
      </div>
      <p>
        <StageTag job={job} />
        <span className="muted small"> · {BAND_LABEL[STAGE_BAND[job.stage]]}</span>
      </p>
      <dl className="jobs-map__facts">
        {job.customerName ? (
          <>
            <dt>Customer</dt>
            <dd>{job.customerName}</dd>
          </>
        ) : null}
        <dt>Address</dt>
        <dd>{addressLine(job) ?? "No address recorded"}</dd>
        <dt>Surveyor</dt>
        <dd>{job.surveyor?.name ?? "Not booked"}</dd>
        <dt>Survey</dt>
        <dd>
          {job.latestSurvey ? (
            <a href={href.survey(job.latestSurvey.id)}>Surveyed {longDate(job.latestSurvey.surveyedOn)}</a>
          ) : (
            "Not surveyed yet"
          )}
        </dd>
        <dt>Created</dt>
        <dd>{longDate(job.createdAt)}</dd>
        <dt>On the map</dt>
        <dd>
          {job.location?.source === "postcode"
            ? "At the property's postcode"
            : job.location?.source === "photo-gps"
              ? "Where the site photographs were taken (no usable postcode)"
              : UNPLACED_WORDS[job.unplacedReason ?? "pending"]}
        </dd>
      </dl>
      <p>
        <a className="cta" href={href.job(job.jobId)}>
          Open the job
        </a>
      </p>
    </aside>
  );
}
